import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";
import { t } from "ttag";

export interface ISlideshowCell {
    thumbnailURL: string;
    thumbnail: string;
    headline: string;
    descript: string;
    label: string;
    styling: string;
}

type IProps = Immutable<{
    images: ISlideshowCell[];
}>;

const buildSlides = (images: IProps["images"]): React.ReactNode => {
    return images.map((img, i) => {
        // thumbnailURL may be empty (image deleted from the Wagtail library);
        // an empty CSS url() would request the current page as an image.
        const slideStyle = img.thumbnailURL
            ? {
                  backgroundImage: `url(${img.thumbnailURL})`,
              }
            : {};
        const slideClasses = classNames({
            "slideshow__cell": true,
            "slideshow__cell--inverse": img.styling !== "",
        });
        return (
            <div key={i} className={slideClasses}>
                <div className="slideshow__background" style={slideStyle}>
                    <div className="slideshow__content">
                        <div className="slideshow__copy">
                            <h3 className="slideshow__headline type_x1point5">
                                {img.headline}
                            </h3>
                            <RichText
                                className="slideshow__descript"
                                html={img.descript}
                            />
                        </div>
                        <div className="slideshow__copy-bg"></div>
                    </div>
                </div>
            </div>
        );
    });
};

const buildSlidesNav = (
    images: IProps["images"],
    selectedIndex: number,
    onSelectSlideNav: (
        slideNum: number,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    return images.map((img, i) => {
        const slideNavClasses = classNames({
            "slideshow__thumb-nav-item": true,
            "slideshow__thumb-nav-item--selected": i === selectedIndex,
        });
        return (
            <li key={i} className={slideNavClasses}>
                <button
                    onClick={(event) => onSelectSlideNav(i, event)}
                    title={img.headline}
                >
                    <img
                        src={img.thumbnail}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <RichText
                        className="slideshow__thumb-nav-label"
                        html={img.label}
                        inline
                    />
                </button>
            </li>
        );
    });
};

export const FlagshipSlideshow = (props: IProps): React.ReactNode => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const slideshowCarousel = React.useRef<HTMLDivElement>(null);

    const { flickityRef } = useFlickity(slideshowCarousel, {
        options: {
            cellSelector: ".slideshow__cell",
            cellAlign: "left",
            contain: true,
            pageDots: false,
            prevNextButtons: false,
            wrapAround: true,
            setGallerySize: false,
            imagesLoaded: true,
        },
        onInit: (flickity) => {
            const onChange = () => {
                setSelectedIndex(flickity.selectedIndex);
            };
            flickity.on("change", onChange);
            return () => flickity.off("change", onChange);
        },
    });

    const onSelectSlideNav = (
        slideNum: number,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        if (flickityRef.current) {
            flickityRef.current.select(slideNum);
        }
    };

    return (
        <div className="slideshow slideshow--thumbs" ref={slideshowCarousel}>
            {buildSlides(props.images)}
            <div className="slideshow__thumb-nav u-list-unstyled">
                <span className="ada-screenreader-only">
                    {t`Activating this element will cause content in the slideshow to be updated`}
                </span>
                <ul>
                    {buildSlidesNav(
                        props.images,
                        selectedIndex,
                        onSelectSlideNav,
                    )}
                </ul>
            </div>
        </div>
    );
};
